import React, { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { companyService } from "../services/api";
import { useAuth } from "../contexts/AuthContext";
import { Building2, Search, Plus, Filter, ChevronLeft, ChevronRight } from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "../components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { toast } from "sonner";

const INDUSTRIES = [
  "Technology", "Finance", "Manufacturing", "Healthcare", "Retail", 
  "Real Estate", "Energy", "Telecommunications", "Transportation", "Construction"
];

const STATUSES = ["active", "dormant", "dissolved"];

export default function CompaniesPage() {
  const navigate = useNavigate();
  const { isAdmin } = useAuth();
  const [companies, setCompanies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [industryFilter, setIndustryFilter] = useState("");
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [creating, setCreating] = useState(false);
  const [newCompany, setNewCompany] = useState({
    company_code: "",
    name: "",
    short_name: "",
    registration_no: "",
    incorporation_date: "",
    status: "active",
    industry: "",
    address: "",
    remarks: ""
  });

  const fetchCompanies = useCallback(async () => {
    setLoading(true);
    try {
      const params = { page, limit: 20 };
      if (search) params.search = search;
      if (statusFilter) params.status = statusFilter;
      if (industryFilter) params.industry = industryFilter;
      
      const response = await companyService.getAll(params);
      setCompanies(response.data.items || []);
      setTotalPages(response.data.pages || 1);
    } catch (error) {
      toast.error("Failed to fetch companies");
    } finally {
      setLoading(false);
    }
  }, [page, search, statusFilter, industryFilter]);

  useEffect(() => {
    fetchCompanies();
  }, [fetchCompanies]);

  useEffect(() => {
    setPage(1);
  }, [search, statusFilter, industryFilter]);

  const handleCreateCompany = async (e) => {
    e.preventDefault();
    setCreating(true);
    try {
      await companyService.create(newCompany);
      toast.success("Company created successfully");
      setShowCreateModal(false);
      setNewCompany({
        company_code: "",
        name: "",
        short_name: "",
        registration_no: "",
        incorporation_date: "",
        status: "active",
        industry: "",
        address: "",
        remarks: ""
      });
      fetchCompanies();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to create company");
    } finally {
      setCreating(false);
    }
  };

  return (
    <div className="p-6 sm:p-8" data-testid="companies-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
        <div>
          <h1 className="text-2xl sm:text-3xl font-semibold text-slate-900">Companies</h1>
          <p className="text-sm text-slate-500 mt-1">Manage corporate registry</p>
        </div>
        <Button
          onClick={() => setShowCreateModal(true)}
          className="btn-primary flex items-center gap-2"
          data-testid="create-company-btn"
        >
          <Plus className="w-4 h-4" />
          New Company
        </Button>
      </div>

      {/* Filters */}
      <div className="enterprise-card p-4 mb-6">
        <div className="flex flex-col sm:flex-row gap-4">
          <div className="flex-1">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <Input
                placeholder="Search companies..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="pl-10 form-input"
                data-testid="company-search-input"
              />
            </div>
          </div>
          <div className="flex gap-3">
            <Select value={statusFilter || "all"} onValueChange={(v) => setStatusFilter(v === "all" ? "" : v)}>
              <SelectTrigger className="w-[140px]" data-testid="status-filter">
                <SelectValue placeholder="All Status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Status</SelectItem>
                {STATUSES.map((status) => (
                  <SelectItem key={status} value={status}>
                    {status.charAt(0).toUpperCase() + status.slice(1)}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select value={industryFilter || "all"} onValueChange={(v) => setIndustryFilter(v === "all" ? "" : v)}>
              <SelectTrigger className="w-[160px]" data-testid="industry-filter">
                <SelectValue placeholder="All Industries" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Industries</SelectItem>
                {INDUSTRIES.map((industry) => (
                  <SelectItem key={industry} value={industry}>
                    {industry}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>
      </div>

      {/* Companies Table */}
      <div className="enterprise-card">
        {loading ? (
          <div className="p-8">
            <div className="animate-pulse space-y-4">
              {[...Array(5)].map((_, i) => (
                <div key={i} className="h-12 bg-slate-100 rounded"></div>
              ))}
            </div>
          </div>
        ) : companies.length === 0 ? (
          <div className="p-8 text-center">
            <Building2 className="w-12 h-12 text-slate-300 mx-auto mb-3" />
            <p className="text-slate-500">No companies found</p>
            <p className="text-sm text-slate-400 mt-1">Try adjusting your search or filters</p>
          </div>
        ) : (
          <>
            <div className="overflow-x-auto">
              <table className="data-table">
                <thead>
                  <tr>
                    <th>Code</th>
                    <th>Company Name</th>
                    <th className="hidden md:table-cell">Short Name</th>
                    <th className="hidden lg:table-cell">Registration</th>
                    <th>Industry</th>
                    <th>Status</th>
                    <th className="hidden sm:table-cell">Incorporation</th>
                  </tr>
                </thead>
                <tbody>
                  {companies.map((company) => (
                    <tr 
                      key={company.id} 
                      className="cursor-pointer"
                      onClick={() => navigate(`/companies/${company.id}`)}
                      data-testid={`company-row-${company.company_code}`}
                    >
                      <td className="font-medium text-blue-900">{company.company_code}</td>
                      <td className="text-slate-900">{company.name}</td>
                      <td className="hidden md:table-cell">{company.short_name || '-'}</td>
                      <td className="hidden lg:table-cell">{company.registration_no || '-'}</td>
                      <td>{company.industry || '-'}</td>
                      <td>
                        <span className={`badge ${
                          company.status === 'active' ? 'badge-active' :
                          company.status === 'dormant' ? 'badge-dormant' : 'badge-dissolved'
                        }`}>
                          {company.status}
                        </span>
                      </td>
                      <td className="hidden sm:table-cell">{company.incorporation_date || '-'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            <div className="px-4 py-3 border-t border-slate-200 flex items-center justify-between">
              <p className="text-sm text-slate-500">
                Page {page} of {totalPages}
              </p>
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setPage(p => Math.max(1, p - 1))}
                  disabled={page === 1}
                  data-testid="prev-page-btn"
                >
                  <ChevronLeft className="w-4 h-4" />
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                  disabled={page === totalPages}
                  data-testid="next-page-btn"
                >
                  <ChevronRight className="w-4 h-4" />
                </Button>
              </div>
            </div>
          </>
        )}
      </div>

      {/* Create Company Modal */}
      <Dialog open={showCreateModal} onOpenChange={setShowCreateModal}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Create New Company</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleCreateCompany}>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 py-4">
              <div>
                <Label className="form-label">Company Code *</Label>
                <Input
                  value={newCompany.company_code}
                  onChange={(e) => setNewCompany({...newCompany, company_code: e.target.value})}
                  required
                  placeholder="e.g., CMP0001"
                  className="form-input"
                  data-testid="new-company-code"
                />
              </div>
              <div>
                <Label className="form-label">Company Name *</Label>
                <Input
                  value={newCompany.name}
                  onChange={(e) => setNewCompany({...newCompany, name: e.target.value})}
                  required
                  placeholder="Full company name"
                  className="form-input"
                  data-testid="new-company-name"
                />
              </div>
              <div>
                <Label className="form-label">Short Name</Label>
                <Input
                  value={newCompany.short_name}
                  onChange={(e) => setNewCompany({...newCompany, short_name: e.target.value})}
                  placeholder="Abbreviated name"
                  className="form-input"
                />
              </div>
              <div>
                <Label className="form-label">Registration No</Label>
                <Input
                  value={newCompany.registration_no}
                  onChange={(e) => setNewCompany({...newCompany, registration_no: e.target.value})}
                  placeholder="Official registration number"
                  className="form-input"
                />
              </div>
              <div>
                <Label className="form-label">Incorporation Date</Label>
                <Input
                  type="date"
                  value={newCompany.incorporation_date}
                  onChange={(e) => setNewCompany({...newCompany, incorporation_date: e.target.value})}
                  className="form-input"
                />
              </div>
              <div>
                <Label className="form-label">Status</Label>
                <Select 
                  value={newCompany.status} 
                  onValueChange={(v) => setNewCompany({...newCompany, status: v})}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {STATUSES.map((status) => (
                      <SelectItem key={status} value={status}>
                        {status.charAt(0).toUpperCase() + status.slice(1)}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="form-label">Industry</Label>
                <Select 
                  value={newCompany.industry} 
                  onValueChange={(v) => setNewCompany({...newCompany, industry: v})}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select industry" />
                  </SelectTrigger>
                  <SelectContent>
                    {INDUSTRIES.map((industry) => (
                      <SelectItem key={industry} value={industry}>
                        {industry}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="sm:col-span-2">
                <Label className="form-label">Address</Label>
                <Input
                  value={newCompany.address}
                  onChange={(e) => setNewCompany({...newCompany, address: e.target.value})}
                  placeholder="Business address"
                  className="form-input"
                />
              </div>
              <div className="sm:col-span-2">
                <Label className="form-label">Remarks</Label>
                <textarea
                  value={newCompany.remarks}
                  onChange={(e) => setNewCompany({...newCompany, remarks: e.target.value})}
                  placeholder="Additional notes"
                  className="form-input min-h-[80px] resize-none"
                />
              </div>
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setShowCreateModal(false)}>
                Cancel
              </Button>
              <Button type="submit" className="btn-primary" disabled={creating} data-testid="submit-new-company">
                {creating ? "Creating..." : "Create Company"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
